import React, { useEffect, useRef, useState } from 'react';
import { InfinitySpin } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { selectRoles } from '../../../Redux/AuthSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import OrderFullDetails from './OrderFullDetails';
import { useOrders } from '../../../context/orderContext';
import exportFromJSON from 'export-from-json';
import Table from '../../dataUi/Table';
import { formatDate } from '../../../utils/dateUtils';
import { FaBox } from 'react-icons/fa';

const OrderInfo = () => {
    const modalRef = useRef();
    const { id, token } = useSelector((state) => state.auth)
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);

    const {
        orders = [],
        loading,
        currentPage,
        limit,
        filter,
        filter_status,
        sortby,
        setFilter,
        setFilter_status,
        fetchOrders
    } = useOrders();

    const [activeTab, setActiveTab] = useState(filter_status);
    const navigate = useNavigate()
    const location = useLocation()
    const requiredRoles = location.state?.requiredRoles || [];

    const toggleShowModal = (event, order) => {
        event.stopPropagation();
        event.preventDefault();
        setSelectedOrder(order);
        setShowModal(true)
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowModal(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        fetchOrders(currentPage, limit)
    }, [id, token, currentPage, limit, filter, filter_status, sortby])

    const switchTab = (value) => {
        setActiveTab(value);

        const statusMap = {
            'pending': 'pending',
            'awaiting-confirmation': 'awaiting-confirmation',
            'preparing-order': 'preparing-order',
            'awaiting-payment': 'awaiting-payment',
            'paid-awaiting-shipping': 'paid-awaiting-shipping',
            'shipped': 'shipped',
            'received': 'received',
            'cancelled': 'cancelled'
        };

        const filterStatus = statusMap[value] || '';
        setFilter_status(filterStatus);
        setFilter(filterStatus ? 'status' : '');
    };

    const showOrderDetail = (row) => {
        const hasDeletedProducts = row?.order_items?.some(item =>
            !item.product_name || item.product_name === 'Product Not Found'
        );

        if (hasDeletedProducts) {
            setSelectedOrder(row);
            setShowModal(true);
        } else {
            navigate(`${row.id}`, { state: { requiredRoles } });
        }
    };

    const selectedRoles = useSelector(selectRoles);
    const roles = Array.isArray(selectedRoles) ? selectedRoles : selectedRoles.split(',').map(role => role.trim());

    const exportFunc = () => {
        const data = orders?.map(o => ({
            id: o.id,
            Product_name: o.order_items?.map(or => or.product_name),
            Retailer: o.retailer.name,
            Status: o.status,
            Completed_by: o?.completed_by?.first_name + " " + o?.completed_by?.last_name,
            Date_created: new Date(o?.created_at).toLocaleDateString()
        }))
        const fileName = 'Orders'
        const exportType = exportFromJSON.types.csv

        exportFromJSON({ data, fileName, fields: ["id", "Product_name", "Retailer", "Status", "Completed_by", "Date_created"], exportType })
    }

    const handleSearch = (value) => {
        setSearchTerm(value);
        if (!value.trim() || !orders) {
            setFilteredOrders(orders || []);
            return;
        }

        const filtered = orders.filter(order =>
            order && (
                (order.id && order.id.toString().includes(value.toLowerCase())) ||
                (order.retailer?.name && order.retailer.name.toLowerCase().includes(value.toLowerCase())) ||
                (order.status && order.status.toLowerCase().includes(value.toLowerCase())) ||
                (order.order_items && order.order_items.some(item =>
                    item.product_name && item.product_name.toLowerCase().includes(value.toLowerCase())
                ))
            )
        );
        setFilteredOrders(filtered);
    };

    useEffect(() => {
        if (orders && Array.isArray(orders)) {
            setFilteredOrders(orders);
        }
    }, [orders]);

    const formatOrdersForTable = () => {
        return orders.map((order, index) => {
            const hasDeletedProducts = order?.order_items?.some(item =>
                item.product_name === null ||
                item.product_name === undefined ||
                item.product_name === 'Product Not Found'
            );
            return {
                number: index + 1,
                product_name: hasDeletedProducts ? "No products found" :
                    order?.order_items?.length > 0
                        ? order.order_items
                            .map(item => item.product_name)
                            .filter(name => name && name !== 'Product Not Found')
                            .join(", ")
                        : "No products found",
                retailer_name: order?.retailer?.name || "No retailer",
                status: hasDeletedProducts ? "deleted" : order.status,
                completed_by: `${order?.completed_by?.first_name || '-'} ${order?.completed_by?.last_name || '-'}`,
                created_at: formatDate(new Date(order?.created_at).toLocaleDateString()),
                _originalData: order
            };
        });
    };

    const tableHeaders = [
        { key: 'number', label: 'NO.', sortable: true },
        { key: 'product_name', label: 'Product name', sortable: false },
        { key: 'retailer_name', label: 'Retailer', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'completed_by', label: 'Completed By', sortable: true },
        { key: 'created_at', label: 'Date created', sortable: true },
    ];

    const tableActions = [
        {
            label: 'View Details',
            variant: 'primary',
            onClick: (row) => {
                toggleShowModal(new Event('click'), row._originalData);
            }
        }
    ];

    const statusConfig = {
        'pending': {
            label: 'Pending',
            className: 'bg-yellow-100 text-yellow-800'
        },
        'awaiting-confirmation': {
            label: 'Awaiting Confirmation',
            className: 'bg-blue-100 text-blue-800'
        },
        'preparing-order': {
            label: 'Preparing Order',
            className: 'bg-purple-100 text-purple-800'
        },
        'awaiting-payment': {
            label: 'Awaiting Payment',
            className: 'bg-orange-100 text-orange-800'
        },
        'paid-awaiting-shipping': {
            label: 'Paid Awaiting Shipping',
            className: 'bg-indigo-100 text-indigo-800'
        },
        'shipped': {
            label: 'Shipped',
            className: 'bg-teal-100 text-teal-800'
        },
        'received': {
            label: 'Received',
            className: 'bg-green-100 text-green-800'
        },
        'cancelled': {
            label: 'Cancelled',
            className: 'bg-red-100 text-red-800'
        },
        'deleted': {
            label: 'Deleted',
            className: 'bg-red-500 text-gray-100'
        }
    };

    const filterOptions = Object.keys(statusConfig).map(key => ({
        label: statusConfig[key].label,
        value: key
    }));

    return (
        <div className='h-full overflow-auto font-lufga'>
            <div className='font-lufga'>
                {loading && (
                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
                        <InfinitySpin color="#008DDA" loading={loading} size={40} />
                    </div>
                )}

                <div className="flex items-center justify-start w-full gap-2 px-4 mb-3 overflow-x-auto font-lufga sm:gap-4 whitespace-nowrap tabs-scrollbar">
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'All' || !filter_status
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('All')}
                    >
                        All
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'pending'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('pending')}
                    >
                        Pending
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'awaiting-confirmation'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('awaiting-confirmation')}
                    >
                        Awaiting confirmation
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'preparing-order'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('preparing-order')}
                    >
                        Preparing
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'awaiting-payment'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('awaiting-payment')}
                    >
                        Awaiting Payment
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'paid-awaiting-shipping'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('paid-awaiting-shipping')}
                    >
                        Paid-Awaiting Shipping
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'shipped'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('shipped')}
                    >
                        Shipped
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'received'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('received')}
                    >
                        Received
                    </button>
                    <button
                        className={`text-xs sm:text-sm cursor-pointer py-2 px-4 text-nowrap ${activeTab === 'cancelled'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500'
                            }`}
                        onClick={() => switchTab('cancelled')}
                    >
                        Cancelled
                    </button>
                </div>

                <div className='mx-6 font-lufga'>
                    {!loading && (
                        <Table
                            headers={tableHeaders}
                            rows={formatOrdersForTable()}
                            actions={tableActions}
                            emptyMessage={searchTerm ? `No results found for "${searchTerm}"` : "No orders available"}
                            headerBgColor="bg-blue-200"
                            onRowClick={(row) => showOrderDetail(row._originalData)}
                            title="Orders"
                            itemsPerPage={limit}
                            searchPlaceholder="Search by ID, retailer, status..."
                            exportButtonText="Export"
                            onExport={exportFunc}
                            filterOptions={filterOptions}
                            statusConfig={statusConfig}
                            className="text-xs"
                        />
                    )}

                    {showModal && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-800 bg-opacity-50">
                            <div ref={modalRef} className="bg-white rounded-md min-h-max max-h-[90vh] w-[672px] overflow-auto shadow-lg p-1">
                                {selectedOrder?.order_items?.some(item => !item.product_name || item.product_name === 'Product Not Found') ? (
                                    <div className="p-4 text-center">
                                        <div className="mb-3">
                                            <FaBox className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                            <h2 className="mb-1 text-lg font-semibold text-gray-800">Order Product Deleted</h2>
                                            <p className="text-sm text-gray-600">This order contains deleted products and cannot be viewed.</p>
                                        </div>
                                        <button
                                            onClick={() => setShowModal(false)}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm"
                                        >
                                            Close
                                        </button>
                                    </div>
                                ) : (
                                    <OrderFullDetails toggle={() => { setShowModal(false) }} order={selectedOrder} />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OrderInfo;
